import { changedGuidanceLines } from "@productlane/lib/src/agent-recommendations"

import type { AgentItem } from "./agent-items"
import { conversationsLabel, docsSourceLabel } from "./format"
import { ideaTitle } from "./idea-list"

export type BatchGroupId = "docs" | "guidance" | "needs-you"

export interface BatchRow {
	item: AgentItem
	title: string
	// What the change does, in one line.
	detail: string
	// Where the agent learned it: GitHub, a teammate, the conversations.
	source: string
	// Accept all applies it; the rest need a person first.
	bulk: boolean
}

export interface BatchGroup {
	id: BatchGroupId
	rows: BatchRow[]
}

function groupOf(item: AgentItem): BatchGroupId {
	if (item.kind === "docs" || item.kind === "proposal") return "docs"
	if (item.kind !== "recommendation") return "needs-you"
	if (item.type === "GUIDANCE") return "guidance"
	if (item.type === "CONTENT" || item.type === "CONTRADICTION") return "docs"
	return "needs-you"
}

function detailOf(item: AgentItem): string {
	switch (item.kind) {
		case "recommendation": {
			const change = item.recommendation.payload.guidance
			const added =
				item.type === "GUIDANCE" && change
					? changedGuidanceLines(change.before, change.after)
							.filter((line) => line.type === "added")
							.map((line) => line.text)
							.join(" ")
					: ""
			// A connector card reads best as what would get built.
			const suggestion =
				item.type === "DATA" || item.type === "ACTION"
					? item.recommendation.payload.guide?.suggestion
					: undefined
			return added || suggestion || item.recommendation.summary
		}
		case "setup":
			return item.step.body
		case "code":
			return item.state === "review"
				? "A fix is ready for your review."
				: "I'm still working on a fix."
		case "docs":
			return "A change to your help center."
		case "proposal":
			return item.row.isOrphanDraft
				? "A draft waiting to be published."
				: "A suggested edit from your team."
	}
}

function sourceOf(item: AgentItem): string {
	switch (item.kind) {
		// Where the gap showed up. Code or a past reply is only how the agent knows the answer.
		case "recommendation":
			return `From ${conversationsLabel(item.recommendation.conversationCount)}`
		case "docs": {
			const threads = item.group.sourceThreads?.length ?? 0
			return item.group.source === "THREAD" && threads > 0
				? `From ${conversationsLabel(threads)}`
				: docsSourceLabel(item.group.source)
		}
		case "proposal":
			return item.row.isOrphanDraft ? "From the API" : "From your team"
		case "setup":
			return "Setup"
		case "code":
			return "From a bug report"
	}
}

/** Every open item in the one message, grouped by what accepting it does. */
export function batchGroups(items: readonly AgentItem[]): BatchGroup[] {
	const groups: Record<BatchGroupId, BatchRow[]> = {
		docs: [],
		guidance: [],
		"needs-you": [],
	}
	for (const item of items) {
		const id = groupOf(item)
		groups[id].push({
			item,
			title: ideaTitle(item),
			detail: detailOf(item),
			source: sourceOf(item),
			bulk: id !== "needs-you",
		})
	}
	return (["docs", "guidance", "needs-you"] as const)
		.map((id) => ({ id, rows: groups[id] }))
		.filter((group) => group.rows.length > 0)
}

/** The ids Accept all sends, split by what the server accepts them as. */
export function batchAcceptInput(
	rows: readonly BatchRow[],
	skipped: ReadonlySet<string>,
): {
	recommendationIds: string[]
	proposalGroupIds: string[]
	proposalIds: string[]
} {
	const input = {
		recommendationIds: [] as string[],
		proposalGroupIds: [] as string[],
		proposalIds: [] as string[],
	}
	for (const { item, bulk } of rows) {
		if (!bulk || skipped.has(item.id)) continue
		if (item.kind === "recommendation") input.recommendationIds.push(item.id)
		else if (item.kind === "docs") input.proposalGroupIds.push(item.group.id)
		else if (item.kind === "proposal") {
			input.proposalIds.push(...item.row.proposals.map((p) => p.id))
		}
	}
	return input
}

export interface BatchDeclineTarget {
	id: string
	recommendationId: string | null
	proposalGroupId: string | null
	proposalIds: string[]
	setupId: string | null
}

const declineTarget = (
	id: string,
	fields: Partial<Omit<BatchDeclineTarget, "id">>,
): BatchDeclineTarget => ({
	id,
	recommendationId: null,
	proposalGroupId: null,
	proposalIds: [],
	setupId: null,
	...fields,
})

/** What Decline closes, one entry per ticked row, like a decline on its own card. */
export function batchDeclineTargets(
	rows: readonly BatchRow[],
	skipped: ReadonlySet<string>,
): BatchDeclineTarget[] {
	const targets: BatchDeclineTarget[] = []
	for (const row of rows) {
		const { item } = row
		if (!isToggleable(row) || skipped.has(item.id)) continue
		if (item.kind === "recommendation") {
			targets.push(
				declineTarget(item.id, {
					recommendationId: item.id,
					proposalGroupId: item.recommendation.proposalGroupId ?? null,
				}),
			)
		} else if (item.kind === "docs") {
			targets.push(declineTarget(item.id, { proposalGroupId: item.group.id }))
		} else if (item.kind === "proposal") {
			targets.push(
				declineTarget(item.id, {
					proposalIds: item.row.proposals.map((p) => p.id),
				}),
			)
		} else if (item.kind === "setup") {
			targets.push(declineTarget(item.id, { setupId: item.step.id }))
		}
	}
	return targets
}

export interface BatchDeclineResult {
	declined: string[]
	failed: string[]
	// What Undo reopens.
	undo: {
		recommendationIds: string[]
		proposalIds: string[]
		setupIds: string[]
	}
}

// Each row stands alone, so one failed decline never blocks the rest.
export async function runBatchDecline(
	targets: readonly BatchDeclineTarget[],
	actions: {
		// Resolves to the proposals it rejected.
		rejectDocs: (target: BatchDeclineTarget) => Promise<string[]>
		dismiss: (recommendationId: string) => Promise<void>
		dismissSetup: (setupId: string) => void
		onError: (error: unknown, target: BatchDeclineTarget) => void
	},
): Promise<BatchDeclineResult> {
	const result: BatchDeclineResult = {
		declined: [],
		failed: [],
		undo: { recommendationIds: [], proposalIds: [], setupIds: [] },
	}
	for (const target of targets) {
		try {
			if (target.proposalGroupId || target.proposalIds.length > 0) {
				result.undo.proposalIds.push(...(await actions.rejectDocs(target)))
			}
			if (target.recommendationId) {
				await actions.dismiss(target.recommendationId)
				result.undo.recommendationIds.push(target.recommendationId)
			}
			if (target.setupId) {
				actions.dismissSetup(target.setupId)
				result.undo.setupIds.push(target.setupId)
			}
			result.declined.push(target.id)
		} catch (error) {
			actions.onError(error, target)
			result.failed.push(target.id)
		}
	}
	return result
}

const plural = (count: number, one: string, many: string) =>
	count === 1 ? `1 ${one}` : `${count} ${many}`

function joinWords(words: readonly string[]): string {
	if (words.length <= 1) return words.join("")
	return `${words.slice(0, -1).join(", ")} and ${words.at(-1)}`
}

// Why each of the rest waits on a person, one sentence per reason.
export function needsYouLines(rows: readonly BatchRow[]): string[] {
	const connectors = rows.filter(
		(row) =>
			row.item.kind === "recommendation" &&
			(row.item.type === "DATA" || row.item.type === "ACTION"),
	)
	const setup = rows.filter((row) => row.item.kind === "setup")
	const fixes = rows.filter(
		(row) => row.item.kind === "code" && row.item.state === "review",
	)
	const lines: string[] = []
	if (connectors.length > 0) {
		const systems = [
			...new Set(
				connectors.flatMap((row) =>
					row.item.kind === "recommendation" &&
					row.item.recommendation.payload.guide?.system
						? [row.item.recommendation.payload.guide.system]
						: [],
				),
			),
		].slice(0, 3)
		const where = systems.length > 0 ? `, like ${joinWords(systems)}` : ""
		lines.push(
			connectors.length === 1
				? `For one question I need access to one of your own systems${where}. An engineer has to connect it first, so I'd file it as a Linear issue.`
				: `For ${connectors.length} questions I need access to your own systems${where}. An engineer has to connect them first, so I'd file each one as a Linear issue.`,
		)
	}
	if (setup.length > 0) {
		lines.push(
			`To finish setting me up: ${joinWords(setup.map((row, i) => (i === 0 ? row.title : row.title.charAt(0).toLowerCase() + row.title.slice(1))))}.`,
		)
	}
	if (fixes.length > 0) {
		lines.push(
			fixes.length === 1
				? "One bug fix is ready for your review."
				: `${fixes.length} bug fixes are ready for your review.`,
		)
	}
	return lines
}

/**
 * What the agent thinks of its own batch: how much it would change, how sure
 * it is from the re-run against the real questions, and what needs a person.
 */
export function batchAssessment(
	rows: readonly BatchRow[],
	skipped: ReadonlySet<string>,
): string[] {
	return [
		...readyAssessment(rows, skipped),
		...needsYouLines(rows.filter((row) => !row.bulk)),
	]
}

/** The agent's view of what Accept all applies: how much, and how sure it is. */
export function readyAssessment(
	rows: readonly BatchRow[],
	skipped: ReadonlySet<string>,
): string[] {
	const chosen = rows.filter((row) => row.bulk && !skipped.has(row.item.id))
	const docs = chosen.filter((row) => groupOf(row.item) === "docs").length
	const guidance = chosen.length - docs
	const lines: string[] = []

	if (chosen.length > 0) {
		const parts = [
			docs > 0 ? plural(docs, "change to the docs", "changes to the docs") : "",
			guidance > 0
				? plural(guidance, "rule for my guidance", "rules for my guidance")
				: "",
		].filter(Boolean)
		lines.push(`I'd make ${parts.join(" and ")}.`)
	}

	const checked = chosen.flatMap((row) =>
		row.item.kind === "recommendation" &&
		(row.item.recommendation.rerunTotal ?? 0) > 0
			? [
					{
						title: row.title,
						answered: row.item.recommendation.rerunAnswered ?? 0,
						total: row.item.recommendation.rerunTotal ?? 0,
					},
				]
			: [],
	)
	const unsure = checked
		.filter((c) => c.answered < c.total)
		.sort((a, b) => a.answered / a.total - b.answered / b.total)
	const [weakest] = unsure
	if (weakest) {
		lines.push(
			`I'm least sure about "${weakest.title}": it answers ${weakest.answered} of ${weakest.total} questions, so take a look before you accept it.`,
		)
	} else if (chosen.length > 0 && checked.length === chosen.length) {
		lines.push(
			"Each one answered every real question when I re-ran them, so I'd accept them all.",
		)
	}

	return lines
}

export interface BatchStats {
	// Changes Accept all applies.
	selected: number
	// Conversations a month they should resolve.
	impact: number
	// Of the selected ones the review re-ran, how many answered every question.
	verified: number
	checked: number
	needsYou: number
}

export function batchStats(
	rows: readonly BatchRow[],
	skipped: ReadonlySet<string>,
): BatchStats {
	const chosen = rows.filter((row) => row.bulk && !skipped.has(row.item.id))
	let impact = 0
	let verified = 0
	let checked = 0
	for (const { item } of chosen) {
		if (item.kind !== "recommendation") continue
		impact += item.recommendation.impactPerMonth
		const total = item.recommendation.rerunTotal ?? 0
		if (total === 0) continue
		checked++
		if ((item.recommendation.rerunAnswered ?? 0) >= total) verified++
	}
	return {
		selected: chosen.length,
		impact,
		verified,
		checked,
		needsYou: rows.filter((row) => !row.bulk).length,
	}
}

/** A connector card with no issue yet. */
function isConnectorRow(row: BatchRow): boolean {
	return (
		row.item.kind === "recommendation" &&
		(row.item.type === "DATA" || row.item.type === "ACTION") &&
		!row.item.recommendation.payload.linearIssue
	)
}

// Setup an engineer does, so it can go to Linear too. Linear itself cannot.
const FILEABLE_SETUP: ReadonlySet<string> = new Set([
	"github",
	"email",
	"widget",
	"domain",
])

/** A row "Create Linear issues" can file: a connector, or engineering setup. */
export function isIssueRow(row: BatchRow): boolean {
	return (
		isConnectorRow(row) ||
		(row.item.kind === "setup" && FILEABLE_SETUP.has(row.item.step.id))
	)
}

export interface BatchIssueTargets {
	recommendationIds: string[]
	setupSteps: { id: string; title: string; body: string; path: string }[]
}

export function batchIssueTargets(
	rows: readonly BatchRow[],
	skipped: ReadonlySet<string>,
): BatchIssueTargets {
	const targets: BatchIssueTargets = { recommendationIds: [], setupSteps: [] }
	for (const row of rows) {
		if (!isIssueRow(row) || skipped.has(row.item.id)) continue
		if (row.item.kind === "setup") {
			const { id, title, body, path } = row.item.step
			targets.setupSteps.push({ id, title, body, path })
		} else {
			targets.recommendationIds.push(row.item.id)
		}
	}
	return targets
}

export interface BatchSection {
	id: string
	rows: BatchRow[]
}

// A long group opens on its first rows; the rest are one click away.
export const COLLAPSED_ROWS = 4

/** Rows "Show N more" hides. One leftover row costs less than the button. */
export function hiddenRowCount(rows: readonly BatchRow[]): number {
	return rows.length > COLLAPSED_ROWS + 1 ? rows.length - COLLAPSED_ROWS : 0
}

export function shownRows(
	rows: readonly BatchRow[],
	expanded: boolean,
): BatchRow[] {
	return expanded || hiddenRowCount(rows) === 0
		? [...rows]
		: rows.slice(0, COLLAPSED_ROWS)
}

/** "Needs you first" split by what the person does: file, set up, or review. */
export function needsYouSections(rows: readonly BatchRow[]): BatchSection[] {
	return [
		{
			id: "connectors",
			rows: rows.filter(isConnectorRow),
		},
		{
			id: "setup",
			rows: rows.filter((row) => row.item.kind === "setup"),
		},
		{
			id: "other",
			rows: rows.filter(
				(row) => !isConnectorRow(row) && row.item.kind !== "setup",
			),
		},
	].filter((section) => section.rows.length > 0)
}

/** X can only tick rows a bulk action applies to. Every setup step can be declined. */
export function isToggleable(row: BatchRow): boolean {
	return row.bulk || isIssueRow(row) || row.item.kind === "setup"
}
